import i from 'i18next'
import { currency } from 'lib/currency'
import { Cart as CartType } from 'lib/hooks/use-cart'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { res } from 'components/Image'
import { State } from 'components/Layout'
import debounce from 'lodash.debounce'
import classnames from 'classnames'
import { mobileMenuMaxWidth } from 'constants/styles'
import { SliderBack } from 'components/svg/SliderBack'
import { SliderForward } from 'components/svg/SliderForward'
import slugify from 'slugify'
import { gtmPush } from 'components/analytics/GTM'

type Props = {
	cart: CartType
}

export const Suggestions: React.FC<Props> = ({ cart }) => {
	const productIds = cart.lineItems.map( l => l.variant.product.id )
	const [ suggestions, setSuggestions ] = useState( [] )
	const { addToCart } = useContext( State )
	const [ adding, setAdding ] = useState( false )

	useEffect( () => {
		if ( productIds.length ) {
			fetch( '/api/suggestions', {
				method: 'post',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					productIds,
				}),
			}).then( ( r ) => r.json() ).then( setSuggestions )
		}
	}, [ /*productIds*/ productIds[ 0 ] ] ) // initial version only uses first

	const scroller = useRef<HTMLDivElement>( null )
	const [ page, setPage ] = useState( 1 )
	const handleScroll = useCallback(
		debounce( ( scroll: number ) => {
			const item = scroller.current?.querySelector( '.product' )?.clientWidth
			if ( item ) setPage( Math.round( scroll / item ) + 1 )
		}, 500 ),
		[],
	)

	const scrollToItem = ( item ) => {
		if ( !scroller.current ) return

		const width = scroller.current.querySelector( '.product' ).clientWidth
		const gap = 7

		scroller.current?.scrollTo({ left: item * ( width + gap ) })
	}

	useEffect( () => {
		if ( page === 1 ) return
		gtmPush({ event: 'scrollCartSuggestions', ecommerce: { detail: { page } } })
	}, [ page ] )

	const notInCartAlready = ( suggestion ) => (
		!cart.lineItems.find( i => i.variant.id === suggestion.variant.id )
	)

	if ( !suggestions.filter( notInCartAlready ).length ) return null

	return (
		<>
			<div className="row small">
				<div>{i.t( 'cart.progress.suggestions' )}</div>
				<div></div>
			</div>
			<div className="container">
				<div
					className="scroller"
					ref={scroller}
					onScroll={( e ) => handleScroll( e.currentTarget.scrollLeft )}
				>
					<div className="content">
						{suggestions.filter( notInCartAlready ).map( ( suggestion, j ) => (
							<div
								key={suggestion.variant.id}
								className={classnames( 'product', { current: j === page - 1 })}
								onClick={() => scrollToItem( j )}
							>
								<Link
									legacyBehavior
									href={`/products/${slugify(
										suggestion.variant.product.productType,
										{ lower: true }
									)}/${suggestion.handle}`}
								>
									<a>
										<picture>
											<img
												src={res( suggestion.variant.image?.originalSrc, '152x' )}
											/>
										</picture>
									</a>
								</Link>
								<div className="title">{suggestion.title}</div>
								<div className="price">
									{!!suggestion.variant.compareAtPriceV2?.amount && (
										<div className="from">
											{currency.format( suggestion.variant.compareAtPriceV2.amount )}
										</div>
									)}
									{currency.format( suggestion.variant.priceV2.amount )}
								</div>
								<button
									disabled={adding}
									onClick={( e ) => {
										e.preventDefault()
										setAdding( true )
										addToCart( suggestion.variant ).then( () => {
											gtmPush({
												event: 'addSuggestionToCart',
												ecommerce: {
													detail: {
														number: j + 1,
														products: [{
															name: suggestion.variant.product.title,
															id: suggestion.variant.product.id.substring( suggestion.variant.product.id.lastIndexOf( '/' ) + 1 ),
															price: suggestion.variant.priceV2.amount,
															brand: 'Ray',
															variant: suggestion.variant.selectedOptions.map( o => o.value ).join( ' / ' ),
														}],
													},
												},
											})
										}).finally( () =>
											setAdding( false )
										)
									}}
								>
									{adding
										? i.t( 'cart.suggestion.adding' )
										: i.t( 'cart.suggestion.add' )}
								</button>
							</div>
						) )}
						<div className="spacer" />
					</div>
				</div>
				<div className="buttons">
					<SliderBack circle={false} />
					<SliderForward circle={false} />
				</div>
			</div>
			<style jsx>{`
				.container {
					position: relative;
					margin: 0 -30px;
					padding: 0 30px;
					overflow: hidden;
				}

				.buttons {
					pointer-events: none;
					position: absolute;
					top: 0;
					left: -8px;
					width: calc( 100% + 16px );
					height: 100%;
					display: grid;
					grid-template-columns: 40px 40px;
					justify-content: space-between;
					align-content: center;
				}
				.buttons :global( svg ) {
					width: 100%;
					transition: opacity .2s;
				}
				.scroller:has( .current:first-child ) + .buttons :global( svg:first-child ) {
					opacity: 0
				}
				.scroller:has( .current:nth-last-child(2) ) + .buttons :global( svg:last-child ) {
					opacity: 0
				}

				.scroller {
					margin: 0 -30px;
					padding: 0 30px;
					overflow-x: auto;
					scroll-snap-type: x mandatory;
					scroll-behavior: smooth;
					scroll-padding-left: 30px;
					-webkit-overflow-scrolling: touch;
					-ms-overflow-style: none;
					scrollbar-width: none;
				}
				.scroller::-webkit-scrollbar {
					display: none;
				}
				.content {
					display: flex;
					gap: 7px;
				}
				.content > * {
					flex-shrink: 0;
				}
				.content .spacer {
					width: ${30 - 7}px;
				}
				.product {
					width: 100%;
					height: 100%;
					background: white;
					scroll-snap-align: start;
					padding: 22px;
					display: grid;
					grid-template-columns: 86px 1fr 50px;
					grid-column-gap: 20px;
					text-decoration: none;
					font-size: 16px;
					line-height: 1.4;
				}
				@media ( max-width: 500px ) {
					.product {
						font-size: 15px;
					}
				}
				.product:not(.current) {
					cursor: pointer;
				}
				.product:not(.current) a {
					pointer-events: none;
				}
				.product a {
					display: contents;
				}
				.product picture {
					margin: -6px;
					display: flex;
					align-items: center;
					justify-content: center;
					padding: 10px 5px;
					grid-row: span 2;
					background-color: #f7f7f7;
				}
				.product picture img {
					height: 70px;
					object-fit: contain;
					mix-blend-mode: multiply;
					image-rendering: -webkit-optimize-contrast;
				}
				.product .price {
					text-align: right;
				}
				.from {
					color: #B8B8B8;
					text-decoration: line-through;
				}
				.product button {
					height: 1.6em;
					align-self: end;
					background-color: transparent;
					border: none;
					cursor: pointer;
					color: inherit;
					padding: 0;
					justify-self: start;
					text-transform: lowercase;
					text-decoration: underline;
					text-underline-offset: 0.2em;
					transition: none;
				}
				.product button[disabled] {
					text-decoration: none;
					color: #8a8f95;
				}

				@media (max-width: ${mobileMenuMaxWidth}px) {
					.product {
						grid-template-columns: 18vw 1fr 36px;
						grid-column-gap: calc(4px + 2vw);
					}
					.title {
						grid-column: span 2;
					}
					button {
						grid-row: 2;
						grid-column: 2/3;
					}
					.price {
						grid-column: 3/4;
						align-self: end;
						margin-bottom: 1px;
					}
				}
			`}</style>
		</>
	)
}
